import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Heart, MessageCircle, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "all", label: "All Posts" },
  { id: "beginner", label: "Beginner Q's" },
  { id: "wins", label: "Wins" },
  { id: "pricing", label: "Pricing" },
  { id: "marketing", label: "Marketing" },
  { id: "reviews", label: "Reviews" },
  { id: "cleaning", label: "Cleaning" },
];

export default function Community() {
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("all");
  const [content, setContent] = useState("");
  const [postCat, setPostCat] = useState("wins");
  const { user } = useAuth();
  const nav = useNavigate();

  const load = () => {
    api.get(`/community/posts${cat === "all" ? "" : `?category=${cat}`}`).then((r) => setPosts(r.data));
  };
  useEffect(load, [cat]);

  const submit = async () => {
    if (!user) return nav("/login");
    if (!content.trim()) return;
    try {
      await api.post("/community/posts", { category: postCat, content });
      setContent("");
      toast.success("Posted to the community");
      load();
    } catch (e) { toast.error("Could not post."); }
  };

  const like = async (id) => {
    if (!user) return nav("/login");
    try {
      await api.post(`/community/posts/${id}/like`);
      load();
    } catch (e) {}
  };

  return (
    <div className="bg-brand-alabaster min-h-screen">
      <Navbar />
      <section className="py-16 max-w-5xl mx-auto px-6 lg:px-10">
        <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">The Community</div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight">Real hosts. Real numbers. <span className="italic text-brand-pink">Real wins.</span></h1>

        <div className="mt-10 grid lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3 space-y-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`community-cat-${c.id}`}
                className={`block w-full text-left px-4 py-2.5 rounded-full text-sm transition ${cat === c.id ? "bg-brand-pink text-white" : "hover:bg-white text-neutral-700"}`}
              >
                {c.label}
              </button>
            ))}
          </aside>

          <div className="lg:col-span-9 space-y-6">
            {/* Composer */}
            <div className="rounded-3xl bg-white p-6 border border-neutral-100">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand-pink mb-3">
                <Sparkles className="w-3.5 h-3.5" /> Share a win, ask a question
              </div>
              <Textarea
                data-testid="community-composer"
                placeholder={user ? "What are you working on today?" : "Log in to share…"}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={3}
                className="rounded-2xl border-neutral-200"
              />
              <div className="flex items-center justify-between mt-3">
                <select
                  value={postCat}
                  onChange={(e) => setPostCat(e.target.value)}
                  data-testid="community-category-select"
                  className="text-sm rounded-full px-3 py-2 border border-neutral-200 bg-white"
                >
                  {CATEGORIES.filter((c) => c.id !== "all").map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <Button
                  onClick={submit}
                  data-testid="community-post-btn"
                  className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white"
                >
                  <Send className="w-4 h-4 mr-2" /> Post
                </Button>
              </div>
            </div>

            {/* Posts */}
            {posts.map((p) => (
              <div key={p.id} data-testid={`post-${p.id}`} className="rounded-3xl bg-white p-6 border border-neutral-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-brand-blush flex items-center justify-center text-brand-pink font-medium">
                    {p.author_name?.[0] || "H"}
                  </div>
                  <div>
                    <div className="font-medium">{p.author_name}</div>
                    <div className="text-xs text-neutral-500 uppercase tracking-widest">{p.category}</div>
                  </div>
                </div>
                <p className="text-neutral-800 leading-relaxed">{p.content}</p>
                <div className="mt-5 flex items-center gap-6 text-sm text-neutral-500">
                  <button
                    onClick={() => like(p.id)}
                    data-testid={`like-${p.id}`}
                    className="inline-flex items-center gap-2 hover:text-brand-pink"
                  >
                    <Heart className="w-4 h-4" /> {p.likes || 0}
                  </button>
                  <span className="inline-flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" /> {p.comments?.length || 0}
                  </span>
                </div>
              </div>
            ))}
            {posts.length === 0 && (
              <div className="text-neutral-500 text-center py-16">No posts yet in this category. Be the first.</div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
